import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { collection, doc, getDoc, getDocs, getFirestore } from 'firebase/firestore';
import { app } from '../../firebase/firebaseConfig';
import RemoteManagementViewer from './RemoteManagementViewer';

// Devices on the same account as `deviceId`, for the device-switch dropdown —
// the cold-boot equivalent of AdminDashboard's scopeDevicesToAccount: one
// accountsInfo scan, then keep the account's families that actually have a
// deviceManagement doc. Orphaned/unknown devices get just themselves.
async function fetchAccountSiblingDevices(deviceId) {
  const db = getFirestore(app);
  const snapshot = await getDocs(collection(db, 'accountsInfo'));
  const account = snapshot.docs
    .map((d) => d.data())
    .find((data) => (data.families || []).includes(deviceId));
  if (!account) return [{ id: deviceId }];

  const existing = await Promise.all(
    (account.families || []).map(async (familyId) => {
      try {
        const deviceSnap = await getDoc(doc(db, 'deviceManagement', familyId));
        return deviceSnap.exists() ? familyId : null;
      } catch (error) {
        console.error(`Could not check deviceManagement for ${familyId}:`, error);
        return null;
      }
    })
  );

  const ids = existing.filter(Boolean);
  if (!ids.includes(deviceId)) {
    ids.unshift(deviceId);
  }
  return ids.map((id) => ({ id }));
}

// /devices/:deviceId/remote — standalone, cold-bootable remote control. The
// URL is the source of truth: the viewer is keyed on the device id (so
// back/forward and refresh restore it), and device switches navigate instead
// of mutating viewer-local state.
function RemoteControlPage() {
  const { deviceId } = useParams();
  const navigate = useNavigate();
  // Render immediately with a single-entry list; account siblings stream in.
  const [allDevices, setAllDevices] = useState(() => [{ id: deviceId }]);

  // The devices list may be scrolled deep when the row link is clicked; this
  // page should always open at the top (also when switching devices). Instant
  // behavior: the app CSS sets scroll-behavior smooth on <html>, which can
  // swallow plain scrollTo calls.
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [deviceId]);

  useEffect(() => {
    let cancelled = false;
    // Back/forward can land on a device from another account: drop the stale
    // sibling list immediately rather than showing the old account's devices.
    setAllDevices((prev) => (prev.some((d) => d.id === deviceId) ? prev : [{ id: deviceId }]));
    fetchAccountSiblingDevices(deviceId).then((list) => {
      if (!cancelled) {
        setAllDevices(list);
      }
    }).catch((error) => {
      // The single-entry list set above stays in place as the fallback.
      console.error(`Could not load account siblings for ${deviceId}:`, error);
    });
    return () => { cancelled = true; };
  }, [deviceId]);

  return (
    <RemoteManagementViewer
      key={deviceId}
      initialDevice={{ id: deviceId }}
      allDevices={allDevices}
      onBack={() => navigate('/devices')}
      onDeviceSwitch={(id) => navigate('/devices/' + id + '/remote')}
    />
  );
}

export default RemoteControlPage;
